import React, { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

const HomeLoanEligibilityCalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState(100000);
  const [otherIncome, setOtherIncome] = useState(0);
  const [existingEMI, setExistingEMI] = useState(0);
  const [interestRate, setInterestRate] = useState(8.5);
  const [tenure, setTenure] = useState(20);
  const [age, setAge] = useState(35);
  const [retirementAge, setRetirementAge] = useState(60);
  const [result, setResult] = useState(null);

  const calculate = () => {
    const totalIncome = monthlyIncome + otherIncome;
    
    // FOIR (Fixed Obligations to Income Ratio) - typically 50-60% allowed
    const maxFOIR = 0.5; // 50%
    const availableForEMI = (totalIncome * maxFOIR) - existingEMI;
    
    // Maximum tenure based on retirement age
    const maxTenure = Math.min(tenure, retirementAge - age);
    const months = maxTenure * 12;
    
    // Calculate maximum loan amount
    const monthlyRate = interestRate / 12 / 100;
    
    // EMI = P × r × (1 + r)^n / ((1 + r)^n - 1)
    // P = EMI × ((1 + r)^n - 1) / (r × (1 + r)^n)
    const maxLoanAmount = availableForEMI * ((Math.pow(1 + monthlyRate, months) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, months)));
    
    // Calculate EMI for the max loan
    const emi = maxLoanAmount * monthlyRate * Math.pow(1 + monthlyRate, months) / (Math.pow(1 + monthlyRate, months) - 1);
    
    // Total payment and interest
    const totalPayment = emi * months;
    const totalInterest = totalPayment - maxLoanAmount;
    
    // Property value estimation (assuming 80% LTV)
    const estimatedPropertyValue = maxLoanAmount / 0.8;
    
    // Conservative estimate (40% FOIR)
    const conservativeAvailable = (totalIncome * 0.4) - existingEMI;
    const conservativeLoan = conservativeAvailable * ((Math.pow(1 + monthlyRate, months) - 1) / (monthlyRate * Math.pow(1 + monthlyRate, months)));

    setResult({
      totalIncome,
      availableForEMI,
      maxLoanAmount: Math.max(0, maxLoanAmount),
      emi,
      maxTenure,
      totalPayment,
      totalInterest,
      estimatedPropertyValue,
      conservativeLoan: Math.max(0, conservativeLoan),
      foirUsed: existingEMI > 0 ? ((existingEMI + emi) / totalIncome * 100) : (emi / totalIncome * 100),
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Home Loan Eligibility Report', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['Monthly Income', formatCurrencyForPDF(monthlyIncome)],
        ['Other Income', formatCurrencyForPDF(otherIncome)],
        ['Total Income', formatCurrencyForPDF(result.totalIncome)],
        ['Existing EMI', formatCurrencyForPDF(existingEMI)],
        ['Interest Rate', `${interestRate}% p.a.`],
        ['Loan Tenure', `${result.maxTenure} years`],
        ['Maximum Loan Eligible', formatCurrencyForPDF(result.maxLoanAmount)],
        ['Monthly EMI', formatCurrencyForPDF(result.emi)],
        ['Total Interest', formatCurrencyForPDF(result.totalInterest)],
        ['Est. Property Value (80% LTV)', formatCurrencyForPDF(result.estimatedPropertyValue)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Home_Loan_Eligibility.pdf');
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="Home Loan Eligibility Calculator"
        description="Free Home Loan Eligibility Calculator - Check maximum loan amount based on income and existing EMIs."
        keywords="home loan eligibility calculator, loan eligibility calculator, maximum loan amount calculator, home loan calculator India, housing loan eligibility"
        canonicalPath="/home-loan-eligibility"
      />
      <div className="calculator-header">
        <h1>🏡 Home Loan Eligibility Calculator</h1>
        <p>Check how much home loan you can get based on your income</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Monthly Net Income (₹)</label>
              <input
                type="number"
                value={monthlyIncome === '' ? '' : monthlyIncome}
                onChange={(e) => setMonthlyIncome(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Other Monthly Income (₹)</label>
              <input
                type="number"
                value={otherIncome === '' ? '' : otherIncome}
                onChange={(e) => setOtherIncome(e.target.value === '' ? '' : Number(e.target.value))}
              />
              <small style={{ color: '#64748b', fontSize: '11px' }}>Rental, spouse income, etc.</small>
            </div>
          </div>

          <div className="input-group">
            <label>Existing EMIs (₹/month)</label>
            <input
              type="number"
              value={existingEMI === '' ? '' : existingEMI}
              onChange={(e) => setExistingEMI(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>Car loan, personal loan, credit card EMIs</small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Interest Rate (% p.a.)</label>
              <input
                type="number"
                step="0.1"
                value={interestRate === '' ? '' : interestRate}
                onChange={(e) => setInterestRate(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Loan Tenure (Years)</label>
              <input
                type="number"
                value={tenure === '' ? '' : tenure}
                onChange={(e) => setTenure(e.target.value === '' ? '' : Number(e.target.value))}
                max="30"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Your Current Age</label>
              <input
                type="number"
                value={age === '' ? '' : age}
                onChange={(e) => setAge(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
            <div className="input-group">
              <label>Retirement Age</label>
              <input
                type="number"
                value={retirementAge === '' ? '' : retirementAge}
                onChange={(e) => setRetirementAge(e.target.value === '' ? '' : Number(e.target.value))}
              />
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Check Eligibility
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                <h3>Maximum Loan Eligible</h3>
                <div className="amount">{formatCurrency(result.maxLoanAmount)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  EMI: {formatCurrency(result.emi)}/month | Tenure: {result.maxTenure} years
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">Total Monthly Income</span>
                  <span className="value">{formatCurrency(result.totalIncome)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Available for EMI (50% FOIR)</span>
                  <span className="value">{formatCurrency(result.availableForEMI)}</span>
                </div>
                <div className="result-item" style={{ background: '#dbeafe' }}>
                  <span className="label">Maximum Loan Amount</span>
                  <span className="value" style={{ color: '#2563eb', fontWeight: '600' }}>{formatCurrency(result.maxLoanAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Monthly EMI</span>
                  <span className="value">{formatCurrency(result.emi)}</span>
                </div>
                <div className="result-item">
                  <span className="label">FOIR (EMI/Income)</span>
                  <span className="value">{result.foirUsed.toFixed(1)}%</span>
                </div>
                <div className="result-item">
                  <span className="label">Total Interest</span>
                  <span className="value" style={{ color: '#ef4444' }}>{formatCurrency(result.totalInterest)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Property You Can Afford (80% LTV)</span>
                  <span className="value" style={{ color: '#10b981' }}>{formatCurrency(result.estimatedPropertyValue)}</span>
                </div>
                
                <div style={{ marginTop: '16px', padding: '12px', background: '#fef3c7', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '8px', color: '#b45309' }}>Conservative Estimate (40% FOIR)</h4>
                  <div className="result-item" style={{ background: 'transparent' }}>
                    <span className="label">Safe Loan Amount</span>
                    <span className="value" style={{ color: '#f59e0b', fontWeight: '600' }}>{formatCurrency(result.conservativeLoan)}</span>
                  </div>
                </div>
              </div>

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download Eligibility Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter your income details to check loan eligibility</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Eligibility Factors</h4>
        <ul>
          <li><strong>FOIR:</strong> Banks allow 40-60% of income for all EMIs combined</li>
          <li><strong>LTV:</strong> Banks typically finance 75-90% of property value</li>
          <li><strong>Age:</strong> Loan tenure limited by retirement age</li>
          <li><strong>Credit Score:</strong> 750+ score gets better rates</li>
          <li>Actual eligibility varies by bank, employment type, and credit history</li>
        </ul>
      </div>
    </div>
  );
};

export default HomeLoanEligibilityCalculator;
